import React, { useEffect, useRef, useState } from "react";
import { Link, Paperclip, Loader2 } from "lucide-react";
import { safeMessageUrl } from "~/functions/messageContent";
import { buttonClass, fieldClass } from "./shared";

const markup = (label, url) => `[${label.replace(/[\[\]\r\n]/g, " ").trim()}](${url.replace(/\(/g, "%28").replace(/\)/g, "%29")})`;

export default function TemplateAttachments({ channel, request, onInsert, disabled, onUploading }) {
  const [showLink, setShowLink] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  function addLink() {
    if (!safeMessageUrl(url.trim())) { setError("Enter a complete http:// or https:// link."); return; }
    onInsert(markup(label.trim() || url.trim(), url.trim()));
    setShowLink(false); setLabel(""); setUrl(""); setError("");
  }

  async function upload(file) {
    if (!file || disabled || uploading) return;
    if (!/\.pdf$/i.test(file.name) || file.size > 10 * 1024 * 1024) { setError("Choose a PDF file no larger than 10 MB."); return; }
    setUploading(true); onUploading(true); setError("");
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await request("post", "/messaging/templates/attachments", form);
      if (alive.current) onInsert("\n" + markup(data.filename, data.url) + "\n");
    } catch (failure) {
      if (alive.current) setError(failure?.response?.data?.error || "Unable to upload the PDF. Please try again.");
    } finally {
      if (alive.current) { setUploading(false); onUploading(false); }
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return <div className="space-y-2 rounded-md border bg-slate-50 p-3">
    <div className="flex flex-wrap gap-2">
      <button type="button" className={buttonClass} disabled={disabled || uploading} onClick={() => { setShowLink(value => !value); setError(""); }}><Link size={14} />Add link</button>
      <button type="button" className={buttonClass} disabled={disabled || uploading} onClick={() => fileRef.current?.click()}>{uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}{uploading ? "Uploading PDF..." : channel === "email" ? "Attach PDF" : "Add PDF download link"}</button>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" disabled={disabled || uploading} onChange={event => upload(event.target.files?.[0])} />
    </div>
    <p className="text-xs text-gray-500">{channel === "email" ? "Uploaded PDFs are attached to the email and include a download link in the message. Maximum 3 PDFs / 15 MB total." : "The recipient can open the PDF using the link in the full app message."}</p>
    {showLink && <div className="space-y-2">
      <label className="block text-xs">Link text<input className={fieldClass} value={label} disabled={disabled} onChange={event => setLabel(event.target.value)} placeholder="Open document" /></label>
      <label className="block text-xs">URL<input className={fieldClass} value={url} disabled={disabled} onChange={event => setUrl(event.target.value)} placeholder="https://..." inputMode="url" /></label>
      <button type="button" className={buttonClass} disabled={disabled} onClick={addLink}>Insert link</button>
    </div>}
    {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
  </div>;
}
